import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Markdown, Modal } from '@Sage/hoi-poi-ui';

import { ConfigActions } from 'actions';
import { DANA_GOLDEN_MINUTE_DEBUG_TOOLS } from 'constants/Features';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

const copilotActivatedDisclaimerPermission = {
    key: 'danaActivatedDisclaimer',
    value: true,
};

const useCopilot = ({ onConfirmDisclaimerSuccess, overrideModalOverlayClassName } = {}) => {
    // ========================================
    // HOOKS
    // ========================================
    const dispatch = useDispatch();

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const copilotActivated = useSelector(
        (state) => state.config.permission?.danaActivated || false,
    );
    const copilotActivatedDisclaimer = useSelector(
        (state) => state.config.permission?.danaActivatedDisclaimer || false,
    );
    const hasCopilotDebugTools = useSelector(
        (state) => state.config?.flagsmith?.[DANA_GOLDEN_MINUTE_DEBUG_TOOLS] || false,
    );

    // ========================================
    // LOCAL STATE & REFS
    // ========================================
    const [copilotModalAfterDisclaimer, setCopilotModalAfterDisclaimer] = useState(null);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const copilotState = useMemo(
        () => ({ copilotActivated, copilotActivatedDisclaimer, hasCopilotDebugTools }),
        [copilotActivated, copilotActivatedDisclaimer, hasCopilotDebugTools],
    );

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const onCancelDisclaimer = useCallback(() => {
        setCopilotModalAfterDisclaimer(null);
    }, []);

    const onConfirmDisclaimer = useCallback(() => {
        // Pass true for updatePermissions to update Redux state immediately after API success
        dispatch(ConfigActions.setDisclaimer(copilotActivatedDisclaimerPermission, true))
            .then((response) => {
                if (response.State === '1') {
                    onConfirmDisclaimerSuccess &&
                        onConfirmDisclaimerSuccess({ copilotModalAfterDisclaimer });
                } else {
                    errorToast({ text: getLiteral('label_dana_disclaimer_error') });
                }
                setCopilotModalAfterDisclaimer(null);
            })
            .catch(() => {
                errorToast({ text: getLiteral('label_dana_disclaimer_error') });
                setCopilotModalAfterDisclaimer(null);
            });
    }, [copilotModalAfterDisclaimer, dispatch, onConfirmDisclaimerSuccess]);

    // Kept for backwards compatibility with consumers that may call it
    const copilotDisclaimerOnClose = useCallback(() => {
        // Permission is now updated immediately in onConfirmDisclaimer
        // This is a no-op but kept for API compatibility
    }, []);

    // ========================================
    // RENDER HELPERS
    // ========================================
    const renderCopilotDisclaimer = useMemo(
        () => (
            <Modal
                cancelText={getLiteral('label_danai_button_deny_tos')}
                className="fm-entity-modal__dana_disclaimer"
                confirmText={getLiteral('label_danai_button_accept_tos')}
                isOpen={!!copilotModalAfterDisclaimer}
                onCancel={onCancelDisclaimer}
                onConfirm={onConfirmDisclaimer}
                onRequestClose={onCancelDisclaimer}
                overlayClassName={overrideModalOverlayClassName}
                title={getLiteral('label_danai_title_accept_tos')}
            >
                <Markdown
                    content={getLiteral('label_danai_text_accept_tos')}
                    overrides={{ a: { target: '_blank' } }}
                />
            </Modal>
        ),
        [
            copilotModalAfterDisclaimer,
            onCancelDisclaimer,
            onConfirmDisclaimer,
            overrideModalOverlayClassName,
        ],
    );

    return {
        ...copilotState,
        copilotDisclaimerOnClose,
        renderCopilotDisclaimer,
        setCopilotModalAfterDisclaimer,
    };
};

export default useCopilot;
